import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAdvisorLine } from "@/lib/useAdvisorLine";
import { Pencil, Phone, MessageSquare, HeartHandshake } from "lucide-react";

const addr = (a) => (typeof a === "string" ? a : a ? [a.line, a.city, a.state, a.zip].filter(Boolean).join(", ") : "");
const REFILL = { 30: "every 30 days", 60: "every 60 days", 90: "every 90 days" };

function Block({ title, testid, onEdit, children }) {
  return (
    <div className="border-t border-[#EFECE4] pt-3 mt-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62]">{title}</p>
        <button type="button" onClick={onEdit} className="text-xs font-semibold text-[#1E3E62] underline inline-flex items-center gap-1" data-testid={`${testid}-edit`}><Pencil className="w-3 h-3" />Edit</button>
      </div>
      <div className="mt-1.5 text-sm text-[#0B192C] space-y-1">{children}</div>
    </div>
  );
}

const Empty = ({ t }) => <p className="text-sm text-[#8A8F98]">{t}</p>;
const Pending = ({ items, testid }) => <p className="text-sm text-[#7A5A12]" data-testid={testid}>Mentioned in your plan search, not confirmed yet: {items.join(", ")}. Open your plan search to pick the exact match.</p>;

export function HouseholdSummary() {
  const line = useAdvisorLine();
  const lineLabel = line?.assigned ? "my agent" : line?.label;
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [docs, setDocs] = useState([]);
  const [meds, setMeds] = useState([]);
  const [prof, setProf] = useState(null);
  const [pending, setPending] = useState({ doctors: [], meds: [] });
  useEffect(() => {
    api.get("/consumer/case").then((r) => {
      const f = r.data?.facts || {};
      const p = (xs) => (xs || []).filter((x) => x.status !== "confirmed").map((x) => x.text);
      setPending({ doctors: p(f.doctors), meds: p(f.meds) });
    }).catch(() => {});
    api.get("/consumer/household-profile").then((r) => setProf(r.data)).catch(() => setProf(null));
    api.get("/consumer/ask/context").then((r) => setCtx(r.data)).catch(() => setCtx({}));
    api.get("/consumer/my-doctors").then((r) => setDocs(r.data.doctors || [])).catch(() => setDocs([]));
    api.get("/consumer/my-meds").then((r) => setMeds(r.data.medications || [])).catch(() => setMeds([]));
  }, []);
  useEffect(() => { if (ctx && window.location.hash === "#my-household") document.getElementById("my-household")?.scrollIntoView(); }, [ctx]);
  if (!ctx) return null;
  const edit = () => navigate("/app/ask?compare=1");
  const cov = ctx.coverage;
  const cands = ctx.candidate_plans || [];
  return (
    <section id="my-household" className="rounded-2xl border border-[#E5E2DA] bg-white p-4 sm:p-5" data-testid="household-summary">
      <h2 className="font-semibold text-[#0B192C] text-base md:text-lg">Saved in My Nauticus</h2>
      {prof?.details && (
        <Block title="My details" testid="hs-details" onEdit={edit}>
          <p data-testid="hs-details-name">{[prof.details.first_name, prof.details.last_name].filter(Boolean).join(" ") || "Name not recorded"}</p>
          <p className="text-[#5B6670]" data-testid="hs-details-contact">{[prof.details.phone, prof.details.email].filter(Boolean).join(" · ") || "No contact details yet"}</p>
          <p className="text-[#5B6670]" data-testid="hs-details-address">{[prof.details.address_line1, prof.details.city, prof.details.state, prof.details.zip_code].filter(Boolean).join(", ") || "No address yet"}</p>
        </Block>
      )}
      <Block title="Current coverage" testid="hs-coverage" onEdit={edit}>
        {cov?.plan_name ? <p data-testid="hs-coverage-plan">{cov.plan_name} <span className="text-[#5B6670]">· {cov.display_id || [cov.contract_id, cov.pbp, cov.segment_id].filter(Boolean).join("-")} · {cov.plan_year || "year not recorded"}</span></p>
          : cov?.reported_text ? <p data-testid="hs-coverage-reported">{cov.reported_text} <span className="text-[#7A5A12]">· self-reported, exact plan not confirmed yet</span></p>
          : <Empty t="No current coverage saved yet." />}
      </Block>
      <Block title="Plans I'm considering" testid="hs-candidates" onEdit={edit}>
        {cands.length ? cands.map((c) => <p key={c.display_id} data-testid={`hs-candidate-${c.display_id}`}>{c.plan_name || c.display_id} <span className="text-[#5B6670]">· {c.display_id} · {c.plan_year}</span></p>) : <Empty t="No plans selected yet." />}
      </Block>
      <Block title="My doctors" testid="hs-doctors" onEdit={edit}>
        {docs.length ? docs.map((d) => <p key={`${d.doctor_key}|${d.location_key}`} data-testid={`hs-doctor-${d.location_key}`}>{d.name}{d.role === "pcp" ? " (PCP)" : ""} <span className="text-[#5B6670]">· {d.location_name || addr(d.address) || "practice location not recorded"}</span></p>) : !pending.doctors.length && <Empty t="No doctors saved yet." />}
        {pending.doctors.length > 0 && <Pending items={pending.doctors} testid="hs-doctors-pending" />}
      </Block>
      <Block title="My medications" testid="hs-meds" onEdit={edit}>
        {meds.length ? meds.map((m) => <p key={m.med_key || m.rxcui} data-testid={`hs-med-${m.rxcui}`}>{m.strength_form || m.name} <span className="text-[#5B6670]">· {m.quantity} {REFILL[m.refill_days] || `every ${m.refill_days} days`}</span></p>) : !pending.meds.length && <Empty t="No medications saved yet." />}
        {pending.meds.length > 0 && <Pending items={pending.meds} testid="hs-meds-pending" />}
      </Block>
      {prof?.shared_notes?.length > 0 && (
        <div className="border-t border-[#EFECE4] pt-3 mt-3" data-testid="hs-shared-notes">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62]">Notes from your agent</p>
          {prof.shared_notes.map((n) => <p key={n.id} className="text-sm text-[#0B192C] mt-1" data-testid={`hs-shared-note-${n.id}`}>{n.body} <span className="text-xs text-[#5B6670]">· {n.author || "Your agent"} · {(n.at || "").slice(0, 10)}</span></p>)}
        </div>
      )}
      <div className="border-t border-[#EFECE4] pt-3 mt-3 flex flex-wrap gap-2" data-testid="hs-agent-actions">
        {line?.phone && <a href={`tel:${line.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#1E3E62] px-4 py-1.5 text-sm font-semibold text-[#1E3E62]" data-testid="hs-call-agent"><Phone className="w-4 h-4" />Call {lineLabel}</a>}
        {line?.phone && line.smsReady && <a href={`sms:${line.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#1E3E62] px-4 py-1.5 text-sm font-semibold text-[#1E3E62]" data-testid="hs-text-agent"><MessageSquare className="w-4 h-4" />Text {lineLabel}</a>}
        {!ctx.agent && <button type="button" onClick={() => navigate("/app/ask")} className="inline-flex items-center gap-1.5 rounded-full border border-[#1E3E62] px-4 py-1.5 text-sm font-semibold text-[#1E3E62]" data-testid="hs-get-help"><HeartHandshake className="w-4 h-4" />Get help from a licensed agent</button>}
      </div>
    </section>
  );
}
